/**
 * Finance transport boundary.
 *
 * Every request and response alias below comes from the generated canonical
 * OpenAPI client — no wire type is written by hand here. Two contract facts
 * shape this module:
 *
 * 1. Finance is COMPANY-scoped. Its documents carry an explicit `branch_id` in
 *    the body where the contract asks for one, so these calls deliberately use
 *    the plain (non-operational) http helpers and never send
 *    `X-Acting-Branch-Id` — see the `branchScope` note in `@/lib/api/http`.
 * 2. No finance operation takes `If-Match`; every state transition is a POST
 *    whose `x-idempotency` policy is `client-key`. Those calls therefore all
 *    take an `idempotencyKey` argument that the caller must source from
 *    `useIdempotentMutation`, so one logical action replays one key.
 *
 * Money and quantities are decimal STRINGS end to end. Nothing in this file
 * ever converts an amount to a JS number.
 *
 * Six single-record reads the contract publishes are deliberately absent —
 * getBankAccount, getBankStatement, getAssetClass, getDepreciationRun,
 * getCreditNote and getGatewayPayment. Each of those collections returns the
 * COMPLETE record in its list page (or, for the credit note, in the response to
 * the write that created it), so the screens render from what they already
 * hold. Adding the reads would issue a second request for bytes the client
 * already has. See the lane report for the screens that would make them earn
 * their place.
 */
import type { components, operations } from "@erp/api-client";

import { apiFetch, apiRequest, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

// ── Shared vocabulary ────────────────────────────────────────────────────

export type CurrencyCode = Schemas["CurrencyCode"];
export type Money = Schemas["Money"];
export type ReportMoney = Schemas["ReportMoney"];

/** Generated pages are deeply readonly; DataTable and react-query want arrays. */
interface WirePage<T> {
  readonly items: readonly T[];
  readonly next_cursor?: string | null;
}

function toPage<T>(page: WirePage<T>): Page<T> {
  return { items: [...page.items], next_cursor: page.next_cursor ?? null };
}

/** A list endpoint that publishes no cursor still returns a plain array. */
function toItems<T>(page: { readonly items: readonly T[] }): T[] {
  return [...page.items];
}

type Query = Record<string, string | number | boolean | null | undefined>;

/** Drop absent filters so `buildQuery` never emits `?status=undefined`. */
function definedQuery(query: Query): Query {
  const result: Query = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") result[key] = value;
  }
  return result;
}

// ── Tax codes ────────────────────────────────────────────────────────────

export type TaxCode = Schemas["TaxCode"];
export type TaxRate = Schemas["TaxRate"];
export type TaxCodeCreate = Schemas["TaxCodeCreate"];
export type TaxCodePatch = Schemas["TaxCodePatch"];
export type TaxRateCreate = Schemas["TaxRateCreate"];
export type TaxCodeKind = Schemas["TaxCodeKindValue"];
export type TaxJurisdiction = Schemas["TaxJurisdiction"];
export type TaxCodeListQuery = NonNullable<operations["listTaxCodes"]["parameters"]["query"]>;

export const TAX_CODE_KINDS = [
  "standard",
  "zero_rated",
  "exempt",
  "reverse_charge",
] as const satisfies readonly TaxCode["kind"][];

/** `zero_rated` and `exempt` must carry a zero rate — the contract refuses otherwise. */
export const ZERO_RATE_KINDS: ReadonlySet<TaxCodeKind> = new Set<TaxCodeKind>([
  "zero_rated",
  "exempt",
]);

export function listTaxCodes(
  query: TaxCodeListQuery = {},
  signal?: AbortSignal,
): Promise<Page<TaxCode>> {
  return apiFetch<Schemas["TaxCodePage"]>("/tax-codes", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function getTaxCode(taxCodeId: string, signal?: AbortSignal): Promise<TaxCode> {
  return apiFetch<TaxCode>(`/tax-codes/${taxCodeId}`, { signal });
}

export function createTaxCode(body: TaxCodeCreate, idempotencyKey: string): Promise<TaxCode> {
  return apiFetch<TaxCode>("/tax-codes", { method: "POST", body, idempotencyKey });
}

export function updateTaxCode(taxCodeId: string, body: TaxCodePatch): Promise<TaxCode> {
  return apiFetch<TaxCode>(`/tax-codes/${taxCodeId}`, { method: "PATCH", body });
}

export function addTaxRate(
  taxCodeId: string,
  body: TaxRateCreate,
  idempotencyKey: string,
): Promise<TaxCode> {
  return apiFetch<TaxCode>(`/tax-codes/${taxCodeId}/rates`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

// ── Supplier invoices (AP) ───────────────────────────────────────────────

export type SupplierInvoice = Schemas["SupplierInvoice"];
export type SupplierInvoiceLine = Schemas["SupplierInvoiceLine"];
export type SupplierInvoiceStatus = Schemas["SupplierInvoiceStatus"];
export type SupplierInvoiceCreate = Schemas["SupplierInvoiceCreate"];
export type SupplierInvoiceCreateLine = Schemas["SupplierInvoiceCreateLine"];
export type MatchDiscrepancy = Schemas["MatchDiscrepancy"];
export type MatchDiscrepancyDecision = Schemas["MatchDiscrepancyDecision"];
export type SupplierInvoiceListQuery = NonNullable<
  operations["listSupplierInvoices"]["parameters"]["query"]
>;

export const SUPPLIER_INVOICE_STATUSES = [
  "draft",
  "matched",
  "discrepancy",
  "posted",
  "paid",
  "void",
] as const satisfies readonly SupplierInvoiceStatus[];

export function listSupplierInvoices(
  query: SupplierInvoiceListQuery = {},
  signal?: AbortSignal,
): Promise<Page<SupplierInvoice>> {
  return apiFetch<Schemas["SupplierInvoicePage"]>("/supplier-invoices", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function getSupplierInvoice(
  supplierInvoiceId: string,
  signal?: AbortSignal,
): Promise<SupplierInvoice> {
  return apiFetch<SupplierInvoice>(`/supplier-invoices/${supplierInvoiceId}`, { signal });
}

export function captureSupplierInvoice(
  body: SupplierInvoiceCreate,
  idempotencyKey: string,
): Promise<SupplierInvoice> {
  return apiFetch<SupplierInvoice>("/supplier-invoices", { method: "POST", body, idempotencyKey });
}

export function matchSupplierInvoice(
  supplierInvoiceId: string,
  idempotencyKey: string,
): Promise<SupplierInvoice> {
  return apiFetch<SupplierInvoice>(`/supplier-invoices/${supplierInvoiceId}/match`, {
    method: "POST",
    idempotencyKey,
  });
}

export function postSupplierInvoice(
  supplierInvoiceId: string,
  idempotencyKey: string,
): Promise<SupplierInvoice> {
  return apiFetch<SupplierInvoice>(`/supplier-invoices/${supplierInvoiceId}/postings`, {
    method: "POST",
    idempotencyKey,
  });
}

export function resolveMatchDiscrepancy(
  supplierInvoiceId: string,
  discrepancyId: string,
  body: MatchDiscrepancyDecision,
  idempotencyKey: string,
): Promise<SupplierInvoice> {
  return apiFetch<SupplierInvoice>(
    `/supplier-invoices/${supplierInvoiceId}/discrepancies/${discrepancyId}/resolution`,
    { method: "POST", body, idempotencyKey },
  );
}

export function overrideMatchDiscrepancy(
  supplierInvoiceId: string,
  discrepancyId: string,
  body: MatchDiscrepancyDecision,
  idempotencyKey: string,
): Promise<SupplierInvoice> {
  return apiFetch<SupplierInvoice>(
    `/supplier-invoices/${supplierInvoiceId}/discrepancies/${discrepancyId}/override`,
    { method: "POST", body, idempotencyKey },
  );
}

// ── Customer invoices, credit notes, receipts (AR) ───────────────────────

export type CustomerInvoice = Schemas["CustomerInvoice"];
export type CustomerInvoiceLine = Schemas["CustomerInvoiceLine"];
export type ARInvoiceStatus = Schemas["ARInvoiceStatus"];
export type CreateCustomerInvoiceRequest = Schemas["CreateCustomerInvoiceRequest"];
export type CreateCustomerInvoiceLine = Schemas["CreateCustomerInvoiceLine"];
export type CreditNote = Schemas["CreditNote"];
export type CreateCreditNoteRequest = Schemas["CreateCreditNoteRequest"];
export type CustomerReceipt = Schemas["CustomerReceipt"];
export type CreateCustomerReceiptRequest = Schemas["CreateCustomerReceiptRequest"];
export type ARReceiptMethod = Schemas["ARReceiptMethod"];
export type AllocateReceiptRequest = Schemas["AllocateReceiptRequest"];
export type AllocationPlan = Schemas["AllocationPlan"];
export type CustomerInvoiceListQuery = NonNullable<
  operations["listCustomerInvoices"]["parameters"]["query"]
>;

export const AR_INVOICE_STATUSES = [
  "draft",
  "posted",
  "partially_paid",
  "paid",
  "void",
] as const satisfies readonly ARInvoiceStatus[];

export const AR_RECEIPT_METHODS = [
  "bank_transfer",
  "card",
  "cash",
  "cheque",
  "online_gateway",
  "other",
] as const satisfies readonly ARReceiptMethod[];

/**
 * `CustomerInvoicePage` publishes no `next_cursor` — this worklist is filtered,
 * not paged, so the transport returns the plain collection the contract sends.
 */
export function listCustomerInvoices(
  query: CustomerInvoiceListQuery = {},
  signal?: AbortSignal,
): Promise<CustomerInvoice[]> {
  return apiFetch<Schemas["CustomerInvoicePage"]>("/customer-invoices", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toItems);
}

export function getCustomerInvoice(
  invoiceId: string,
  signal?: AbortSignal,
): Promise<CustomerInvoice> {
  return apiFetch<CustomerInvoice>(`/customer-invoices/${invoiceId}`, { signal });
}

export function createCustomerInvoice(
  body: CreateCustomerInvoiceRequest,
  idempotencyKey: string,
): Promise<CustomerInvoice> {
  return apiFetch<CustomerInvoice>("/customer-invoices", { method: "POST", body, idempotencyKey });
}

export function finalizeCustomerInvoice(
  invoiceId: string,
  idempotencyKey: string,
): Promise<CustomerInvoice> {
  return apiFetch<CustomerInvoice>(`/customer-invoices/${invoiceId}/finalize`, {
    method: "POST",
    idempotencyKey,
  });
}

export function createCreditNote(
  body: CreateCreditNoteRequest,
  idempotencyKey: string,
): Promise<CreditNote> {
  return apiFetch<CreditNote>("/credit-notes", { method: "POST", body, idempotencyKey });
}

/** `listCreditNotes` — GET · finance.credit-note.read. Without it a draft
 *  credit note is unreachable once its creator navigates away. */
export function listCreditNotes(
  query: { invoice_id?: string; party_id?: string; limit?: number; cursor?: string } = {},
  signal?: AbortSignal,
): Promise<Page<CreditNote>> {
  return apiFetch<Page<CreditNote>>("/credit-notes", { query: definedQuery({ ...query }), signal });
}

export function finalizeCreditNote(
  creditNoteId: string,
  idempotencyKey: string,
): Promise<CreditNote> {
  return apiFetch<CreditNote>(`/credit-notes/${creditNoteId}/finalize`, {
    method: "POST",
    idempotencyKey,
  });
}

export function createCustomerReceipt(
  body: CreateCustomerReceiptRequest,
  idempotencyKey: string,
): Promise<CustomerReceipt> {
  return apiFetch<CustomerReceipt>("/customer-receipts", { method: "POST", body, idempotencyKey });
}

/** `listCustomerReceipts` — GET · finance.customer-receipt.read. */
export function listCustomerReceipts(
  query: { party_id?: string; branch_id?: string; limit?: number; cursor?: string } = {},
  signal?: AbortSignal,
): Promise<Page<CustomerReceipt>> {
  return apiFetch<Page<CustomerReceipt>>("/customer-receipts", {
    query: definedQuery({ ...query }),
    signal,
  });
}

export function getCustomerReceipt(
  receiptId: string,
  signal?: AbortSignal,
): Promise<CustomerReceipt> {
  return apiFetch<CustomerReceipt>(`/customer-receipts/${receiptId}`, { signal });
}

/** PUT: the body is the COMPLETE intended allocation set, never a delta. */
export function allocateCustomerReceipt(
  receiptId: string,
  body: AllocateReceiptRequest,
): Promise<AllocationPlan> {
  return apiFetch<AllocationPlan>(`/customer-receipts/${receiptId}/allocations`, {
    method: "PUT",
    body,
  });
}

// ── Payment runs ─────────────────────────────────────────────────────────

export type PaymentRun = Schemas["PaymentRun"];
export type PaymentRunLine = Schemas["PaymentRunLine"];
export type PaymentRunStatus = Schemas["PaymentRunStatus"];
export type PaymentRunCreate = Schemas["PaymentRunCreate"];
export type PaymentRunSubmission = Schemas["PaymentRunSubmission"];
export type PaymentFileRequest = Schemas["PaymentFileRequest"];
export type PaymentFile = Schemas["PaymentFile"];

export function proposePaymentRun(
  body: PaymentRunCreate,
  idempotencyKey: string,
): Promise<PaymentRun> {
  return apiFetch<PaymentRun>("/payment-runs", { method: "POST", body, idempotencyKey });
}

/** `listPaymentRuns` — GET · finance.payment-run.read. The run history the
 *  propose response could not answer. */
export function listPaymentRuns(
  query: { status?: string; branch_id?: string; limit?: number; cursor?: string } = {},
  signal?: AbortSignal,
): Promise<Page<PaymentRun>> {
  return apiFetch<Page<PaymentRun>>("/payment-runs", { query: definedQuery({ ...query }), signal });
}

export function getPaymentRun(paymentRunId: string, signal?: AbortSignal): Promise<PaymentRun> {
  return apiFetch<PaymentRun>(`/payment-runs/${paymentRunId}`, { signal });
}

export function submitPaymentRun(
  paymentRunId: string,
  body: PaymentRunSubmission,
  idempotencyKey: string,
): Promise<PaymentRun> {
  return apiFetch<PaymentRun>(`/payment-runs/${paymentRunId}/submission`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function postPaymentRun(paymentRunId: string, idempotencyKey: string): Promise<PaymentRun> {
  return apiFetch<PaymentRun>(`/payment-runs/${paymentRunId}/postings`, {
    method: "POST",
    idempotencyKey,
  });
}

export function exportPaymentRunFile(
  paymentRunId: string,
  body: PaymentFileRequest,
  idempotencyKey: string,
): Promise<PaymentFile> {
  return apiFetch<PaymentFile>(`/payment-runs/${paymentRunId}/payment-file`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

// ── Bank accounts, statements and reconciliation ─────────────────────────

export type BankAccount = Schemas["BankAccount"];
export type BankAccountWrite = Schemas["BankAccountWrite"];
export type BankStatement = Schemas["BankStatement"];
export type BankStatementFormat = Schemas["BankStatementFormat"];
export type BankImportStatus = Schemas["BankImportStatus"];
export type BankStatementImportRequest = Schemas["BankStatementImportRequest"];
export type BankStatementImportResult = Schemas["BankStatementImportResult"];
export type BankStatementLine = Schemas["BankStatementLine"];
export type BankLineStatus = Schemas["BankLineStatus"];
export type BankLineMatch = Schemas["BankLineMatch"];
export type BankLineMatchWrite = Schemas["BankLineMatchWrite"];
export type BankLineMatchResult = Schemas["BankLineMatchResult"];
export type BankLineExclusion = Schemas["BankLineExclusion"];
export type BankMatchSuggestion = Schemas["BankMatchSuggestion"];
export type BankDocumentType = Schemas["BankDocumentType"];
export type BankImportProfile = Schemas["BankImportProfile"];
export type BankMoney = Schemas["BankMoney"];
export type BankAccountListQuery = NonNullable<
  operations["listBankAccounts"]["parameters"]["query"]
>;
export type BankStatementListQuery = NonNullable<
  operations["listBankStatements"]["parameters"]["query"]
>;
export type BankStatementLineListQuery = NonNullable<
  operations["listBankStatementLines"]["parameters"]["query"]
>;

export const BANK_STATEMENT_FORMATS = [
  "csv",
  "ofx",
  "camt053",
] as const satisfies readonly BankStatementFormat[];

export const BANK_LINE_STATUSES = [
  "imported",
  "suggested",
  "matched",
  "posted",
  "excluded",
] as const satisfies readonly BankLineStatus[];

export const BANK_DOCUMENT_TYPES = [
  "customer_receipt",
  "supplier_payment",
  "customer_invoice",
  "supplier_invoice",
  "gateway_payout",
  "posting_rule",
] as const satisfies readonly BankDocumentType[];

export function listBankAccounts(
  query: BankAccountListQuery = {},
  signal?: AbortSignal,
): Promise<Page<BankAccount>> {
  return apiFetch<Schemas["BankAccountPage"]>("/bank-accounts", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function createBankAccount(
  body: BankAccountWrite,
  idempotencyKey: string,
): Promise<BankAccount> {
  return apiFetch<BankAccount>("/bank-accounts", { method: "POST", body, idempotencyKey });
}

export function replaceBankAccount(
  bankAccountId: string,
  body: BankAccountWrite,
): Promise<BankAccount> {
  return apiFetch<BankAccount>(`/bank-accounts/${bankAccountId}`, { method: "PUT", body });
}

export function listBankImportProfiles(
  bankAccountId: string,
  signal?: AbortSignal,
): Promise<BankImportProfile[]> {
  return apiFetch<Schemas["BankImportProfileList"]>(
    `/bank-accounts/${bankAccountId}/import-profiles`,
    { query: { limit: 200 }, signal },
  ).then(toItems);
}

export function importBankStatement(
  bankAccountId: string,
  body: BankStatementImportRequest,
  idempotencyKey: string,
): Promise<BankStatementImportResult> {
  return apiFetch<BankStatementImportResult>(`/bank-accounts/${bankAccountId}/statement-imports`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function listBankStatements(
  query: BankStatementListQuery = {},
  signal?: AbortSignal,
): Promise<Page<BankStatement>> {
  return apiFetch<Schemas["BankStatementPage"]>("/bank-statements", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function listBankStatementLines(
  query: BankStatementLineListQuery = {},
  signal?: AbortSignal,
): Promise<Page<BankStatementLine>> {
  return apiFetch<Schemas["BankStatementLinePage"]>("/bank-statement-lines", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function getBankStatementLine(
  statementLineId: string,
  signal?: AbortSignal,
): Promise<BankLineMatchResult> {
  return apiFetch<BankLineMatchResult>(`/bank-statement-lines/${statementLineId}`, { signal });
}

export function listBankMatchSuggestions(
  statementLineId: string,
  signal?: AbortSignal,
): Promise<BankMatchSuggestion[]> {
  return apiFetch<Schemas["BankMatchSuggestionList"]>(
    `/bank-statement-lines/${statementLineId}/match-suggestions`,
    { signal },
  ).then(toItems);
}

export function refreshBankMatchSuggestions(
  statementLineId: string,
  idempotencyKey: string,
): Promise<BankMatchSuggestion[]> {
  return apiFetch<Schemas["BankMatchSuggestionList"]>(
    `/bank-statement-lines/${statementLineId}/match-suggestions`,
    { method: "POST", idempotencyKey },
  ).then(toItems);
}

/** PUT: replaces the line's WHOLE split; the parts must sum to the line exactly. */
export function replaceBankLineMatches(
  statementLineId: string,
  body: BankLineMatchWrite,
): Promise<BankLineMatchResult> {
  return apiFetch<BankLineMatchResult>(`/bank-statement-lines/${statementLineId}/matches`, {
    method: "PUT",
    body,
  });
}

export function excludeBankStatementLine(
  statementLineId: string,
  body: BankLineExclusion,
  idempotencyKey: string,
): Promise<BankStatementLine> {
  return apiFetch<BankStatementLine>(`/bank-statement-lines/${statementLineId}/exclusion`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function postBankStatementLine(
  statementLineId: string,
  idempotencyKey: string,
): Promise<BankStatementLine> {
  return apiFetch<BankStatementLine>(`/bank-statement-lines/${statementLineId}/posting`, {
    method: "POST",
    idempotencyKey,
  });
}

// ── Fixed assets and depreciation ────────────────────────────────────────

export type AssetClass = Schemas["AssetClass"];
export type AssetClassWrite = Schemas["AssetClassWrite"];
export type FixedAsset = Schemas["FixedAsset"];
export type FixedAssetStatus = Schemas["FixedAssetStatus"];
export type FixedAssetCreate = Schemas["FixedAssetCreate"];
export type FixedAssetDisposalCreate = Schemas["FixedAssetDisposalCreate"];
export type FixedAssetDisposal = Schemas["FixedAssetDisposal"];
export type FixedAssetEstimateRevision = Schemas["FixedAssetEstimateRevision"];
export type FixedAssetEstimateRevisionResult = Schemas["FixedAssetEstimateRevisionResult"];
export type DepreciationSchedule = Schemas["DepreciationSchedule"];
export type DepreciationScheduleLine = Schemas["DepreciationScheduleLine"];
export type DepreciationMethod = Schemas["DepreciationMethod"];
export type DepreciationRun = Schemas["DepreciationRun"];
export type DepreciationRunCreate = Schemas["DepreciationRunCreate"];
export type FixedAssetListQuery = NonNullable<operations["listFixedAssets"]["parameters"]["query"]>;

export const DEPRECIATION_METHODS = [
  "straight_line",
  "declining_balance",
] as const satisfies readonly DepreciationMethod[];

export const FIXED_ASSET_STATUSES = [
  "draft",
  "active",
  "disposed",
] as const satisfies readonly FixedAssetStatus[];

export function listAssetClasses(signal?: AbortSignal): Promise<Page<AssetClass>> {
  return apiFetch<Schemas["AssetClassPage"]>("/asset-classes", {
    query: { limit: 200 },
    signal,
  }).then(toPage);
}

export function createAssetClass(
  body: AssetClassWrite,
  idempotencyKey: string,
): Promise<AssetClass> {
  return apiFetch<AssetClass>("/asset-classes", { method: "POST", body, idempotencyKey });
}

export function replaceAssetClass(
  assetClassId: string,
  body: AssetClassWrite,
): Promise<AssetClass> {
  return apiFetch<AssetClass>(`/asset-classes/${assetClassId}`, { method: "PUT", body });
}

export function listFixedAssets(
  query: FixedAssetListQuery = {},
  signal?: AbortSignal,
): Promise<Page<FixedAsset>> {
  return apiFetch<Schemas["FixedAssetPage"]>("/fixed-assets", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function getFixedAsset(fixedAssetId: string, signal?: AbortSignal): Promise<FixedAsset> {
  return apiFetch<FixedAsset>(`/fixed-assets/${fixedAssetId}`, { signal });
}

export function capitalizeFixedAsset(
  body: FixedAssetCreate,
  idempotencyKey: string,
): Promise<FixedAsset> {
  return apiFetch<FixedAsset>("/fixed-assets", { method: "POST", body, idempotencyKey });
}

export function getDepreciationSchedule(
  fixedAssetId: string,
  signal?: AbortSignal,
): Promise<DepreciationSchedule> {
  return apiFetch<DepreciationSchedule>(`/fixed-assets/${fixedAssetId}/depreciation-schedule`, {
    signal,
  });
}

export function reviseFixedAssetEstimates(
  fixedAssetId: string,
  body: FixedAssetEstimateRevision,
  idempotencyKey: string,
): Promise<FixedAssetEstimateRevisionResult> {
  return apiFetch<FixedAssetEstimateRevisionResult>(
    `/fixed-assets/${fixedAssetId}/estimate-revisions`,
    { method: "POST", body, idempotencyKey },
  );
}

export function disposeFixedAsset(
  fixedAssetId: string,
  body: FixedAssetDisposalCreate,
  idempotencyKey: string,
): Promise<FixedAssetDisposal> {
  return apiFetch<FixedAssetDisposal>(`/fixed-assets/${fixedAssetId}/disposal`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function listDepreciationRuns(signal?: AbortSignal): Promise<Page<DepreciationRun>> {
  return apiFetch<Schemas["DepreciationRunPage"]>("/depreciation-runs", {
    query: { limit: 100 },
    signal,
  }).then(toPage);
}

export function executeDepreciationRun(
  body: DepreciationRunCreate,
  idempotencyKey: string,
): Promise<DepreciationRun> {
  return apiFetch<DepreciationRun>("/depreciation-runs", { method: "POST", body, idempotencyKey });
}

// ── Period close and year end ────────────────────────────────────────────

export type PeriodCloseChecklist = Schemas["PeriodCloseChecklist"];
export type PeriodCloseTask = Schemas["PeriodCloseTask"];
export type CloseTaskStatus = Schemas["CloseTaskStatus"];
export type CloseTaskDecision = Schemas["CloseTaskDecision"];
export type PeriodCloseTransition = Schemas["PeriodCloseTransition"];
export type PeriodReopen = Schemas["PeriodReopen"];
export type ClosePeriod = Schemas["ClosePeriod"];
export type ClosePeriodStatus = Schemas["ClosePeriodStatus"];
export type YearEndClose = Schemas["YearEndClose"];
export type YearEndCloseCreate = Schemas["YearEndCloseCreate"];

export const PERIOD_REOPEN_REASONS = [
  "authorized_reopen",
  "controlled_correction",
  "migration",
] as const satisfies readonly PeriodReopen["reason_code"][];

export function getPeriodCloseChecklist(
  fiscalPeriodId: string,
  signal?: AbortSignal,
): Promise<PeriodCloseChecklist> {
  return apiFetch<PeriodCloseChecklist>(`/fiscal-periods/${fiscalPeriodId}/close-checklist`, {
    signal,
  });
}

export function runPeriodCloseChecks(
  fiscalPeriodId: string,
  idempotencyKey: string,
): Promise<PeriodCloseChecklist> {
  return apiFetch<PeriodCloseChecklist>(`/fiscal-periods/${fiscalPeriodId}/close-checklist/run`, {
    method: "POST",
    idempotencyKey,
  });
}

export function decideCloseTask(
  closeTaskId: string,
  body: CloseTaskDecision,
  idempotencyKey: string,
): Promise<PeriodCloseTask> {
  return apiFetch<PeriodCloseTask>(`/close-tasks/${closeTaskId}/decision`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function transitionFiscalPeriodClose(
  fiscalPeriodId: string,
  body: PeriodCloseTransition,
  idempotencyKey: string,
): Promise<ClosePeriod> {
  return apiFetch<ClosePeriod>(`/fiscal-periods/${fiscalPeriodId}/close-transition`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function reopenFiscalPeriod(
  fiscalPeriodId: string,
  body: PeriodReopen,
  idempotencyKey: string,
): Promise<ClosePeriod> {
  return apiFetch<ClosePeriod>(`/fiscal-periods/${fiscalPeriodId}/reopen`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function getYearEndClose(fiscalYearId: string, signal?: AbortSignal): Promise<YearEndClose> {
  return apiFetch<YearEndClose>(`/fiscal-years/${fiscalYearId}/year-end-close`, { signal });
}

/**
 * 201 posts the sweep; 202 means the company's approval policy routed it and
 * nothing has posted yet. The caller must branch on `status`, so this is the
 * one finance write that reads the response status rather than only its body.
 */
export async function executeYearEndClose(
  body: YearEndCloseCreate,
  idempotencyKey: string,
): Promise<{ status: number; close: YearEndClose | null }> {
  const response = await apiRequest<YearEndClose>("/year-end-closes", {
    method: "POST",
    body,
    idempotencyKey,
  });
  return { status: response.status, close: response.status === 201 ? response.data : null };
}

// ── Payment gateway ──────────────────────────────────────────────────────

export type GatewayPayment = Schemas["GatewayPayment"];
export type GatewayPaymentStatus = Schemas["GatewayPaymentStatus"];
export type GatewayPaymentException = Schemas["GatewayPaymentException"];
export type GatewayExceptionResolution = Schemas["GatewayExceptionResolution"];
export type GatewayPayout = Schemas["GatewayPayout"];
export type GatewayPayoutStatus = Schemas["GatewayPayoutStatus"];
export type GatewayPayoutReconciliation = Schemas["GatewayPayoutReconciliation"];
export type GatewayEvent = Schemas["GatewayEvent"];
export type GatewayPaymentListQuery = NonNullable<
  operations["listGatewayPayments"]["parameters"]["query"]
>;
export type GatewayPayoutListQuery = NonNullable<
  operations["listGatewayPayouts"]["parameters"]["query"]
>;
export type GatewayExceptionListQuery = NonNullable<
  operations["listGatewayPaymentExceptions"]["parameters"]["query"]
>;

export const GATEWAY_PAYMENT_STATUSES = [
  "pending",
  "matched",
  "exception",
  "settled",
] as const satisfies readonly GatewayPaymentStatus[];

export const GATEWAY_PAYOUT_STATUSES = [
  "pending",
  "reconciled",
] as const satisfies readonly GatewayPayoutStatus[];

export function listGatewayPayments(
  query: GatewayPaymentListQuery = {},
  signal?: AbortSignal,
): Promise<Page<GatewayPayment>> {
  return apiFetch<Schemas["GatewayPaymentPage"]>("/gateway-payments", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function listGatewayPaymentExceptions(
  query: GatewayExceptionListQuery = {},
  signal?: AbortSignal,
): Promise<Page<GatewayPaymentException>> {
  return apiFetch<Schemas["GatewayPaymentExceptionPage"]>("/gateway-payment-exceptions", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function resolveGatewayPaymentException(
  gatewayExceptionId: string,
  body: GatewayExceptionResolution,
  idempotencyKey: string,
): Promise<GatewayPaymentException> {
  return apiFetch<GatewayPaymentException>(
    `/gateway-payment-exceptions/${gatewayExceptionId}/resolution`,
    { method: "POST", body, idempotencyKey },
  );
}

export function replayGatewayEvent(
  gatewayEventId: string,
  idempotencyKey: string,
): Promise<GatewayEvent> {
  return apiFetch<GatewayEvent>(`/gateway-events/${gatewayEventId}/replay`, {
    method: "POST",
    idempotencyKey,
  });
}

export function listGatewayPayouts(
  query: GatewayPayoutListQuery = {},
  signal?: AbortSignal,
): Promise<Page<GatewayPayout>> {
  return apiFetch<Schemas["GatewayPayoutPage"]>("/gateway-payouts", {
    query: definedQuery({ ...query }),
    signal,
  }).then(toPage);
}

export function reconcileGatewayPayout(
  gatewayPayoutId: string,
  body: GatewayPayoutReconciliation,
  idempotencyKey: string,
): Promise<GatewayPayout> {
  return apiFetch<GatewayPayout>(`/gateway-payouts/${gatewayPayoutId}/reconciliation`, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

// ── Reports ──────────────────────────────────────────────────────────────

export type ARAgingReport = Schemas["ARAgingReport"];
export type APAgingReport = Schemas["APAgingReport"];
export type ARAgingBucketKey = Schemas["ARAgingBucketKey"];
export type APAgingBucketName = Schemas["APAgingBucketName"];
export type ARAgingReportQuery = operations["getARAgingReport"]["parameters"]["query"];
export type APAgingReportQuery = operations["getAPAgingReport"]["parameters"]["query"];

export const AGING_BUCKETS = [
  "current",
  "1_30",
  "31_60",
  "61_90",
  "90_plus",
] as const satisfies readonly ARAgingBucketKey[];

export function getARAgingReport(
  query: ARAgingReportQuery,
  signal?: AbortSignal,
): Promise<ARAgingReport> {
  return apiFetch<ARAgingReport>("/reports/ar-aging", {
    query: definedQuery({ ...query }),
    signal,
  });
}

export function getAPAgingReport(
  query: APAgingReportQuery,
  signal?: AbortSignal,
): Promise<APAgingReport> {
  return apiFetch<APAgingReport>("/reports/ap-aging", {
    query: definedQuery({ ...query }),
    signal,
  });
}

// ── Cross-domain reference lookups ───────────────────────────────────────
//
// Finance forms name general-ledger accounts, parties and fiscal periods.
// Those collections belong to the ledger and partners contracts; finance only
// READS them, under their own permissions, and every caller gates the read on
// the corresponding permission so a user without it simply gets no picker.

export type Account = Schemas["Account"];
export type PartySummary = Schemas["PartnersPartySummary"];
export type PartyRole = Schemas["PartnersPartyRole"];
export type FiscalPeriod = Schemas["FiscalPeriod"];
export type FiscalYear = Schemas["FiscalYear"];

export function listPostableAccounts(signal?: AbortSignal): Promise<Account[]> {
  return apiFetch<Schemas["AccountPage"]>("/accounts", {
    query: { limit: 200, active: true },
    signal,
  }).then(toItems);
}

export function listParties(role: PartyRole, signal?: AbortSignal): Promise<PartySummary[]> {
  return apiFetch<Schemas["PartnersPartyPage"]>("/parties", {
    query: { limit: 200, role, status: "active" },
    signal,
  }).then(toItems);
}

export function listFiscalPeriods(signal?: AbortSignal): Promise<FiscalPeriod[]> {
  return apiFetch<Schemas["FiscalPeriodPage"]>("/fiscal-periods", {
    query: { limit: 200 },
    signal,
  }).then(toItems);
}

export function listFiscalYears(signal?: AbortSignal): Promise<FiscalYear[]> {
  return apiFetch<Schemas["FiscalYearPage"]>("/fiscal-years", {
    query: { limit: 200 },
    signal,
  }).then(toItems);
}

/**
 * Procurement references a captured supplier invoice line can point at. A line
 * is either purchase-order linked or expense coded, never both, and omitting
 * the receipt on a procurement line raises a `missing_receipt` finding rather
 * than being ignored — so the capture screen has to be able to name a real GRN
 * line, which means reading the purchasing collections (under purchasing's own
 * permissions) instead of asking a clerk to paste a UUID.
 */
export type PurchaseOrderSummary = Schemas["PurchasingPurchaseOrderSummary"];
export type PurchaseOrder = Schemas["PurchasingPurchaseOrder"];
export type PurchaseOrderLine = Schemas["PurchasingPurchaseOrderLine"];
export type GrnSummary = Schemas["PurchasingGrnSummary"];
export type Grn = Schemas["PurchasingGrn"];
export type GrnLine = Schemas["PurchasingGrnLine"];

export function listPurchaseOrders(
  supplierId: string,
  signal?: AbortSignal,
): Promise<PurchaseOrderSummary[]> {
  return apiFetch<Schemas["PurchasingPurchaseOrderPage"]>("/purchase-orders", {
    query: { limit: 100, supplier_id: supplierId, sort: "-order_date" },
    signal,
  }).then(toItems);
}

export function getPurchaseOrder(
  purchaseOrderId: string,
  signal?: AbortSignal,
): Promise<PurchaseOrder> {
  return apiFetch<PurchaseOrder>(`/purchase-orders/${purchaseOrderId}`, { signal });
}

export function listGrns(purchaseOrderId: string, signal?: AbortSignal): Promise<GrnSummary[]> {
  return apiFetch<Schemas["PurchasingGrnPage"]>("/grns", {
    query: { limit: 100, purchase_order_id: purchaseOrderId },
    signal,
  }).then(toItems);
}

export function getGrn(grnId: string, signal?: AbortSignal): Promise<Grn> {
  return apiFetch<Grn>(`/grns/${grnId}`, { signal });
}
